import { ObservableMap } from "./initiatives.service";
import { Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import deburr from "lodash.deburr";
import * as R from "ramda";
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, Subscription } from "rxjs";
import { filter, first, map, share, shareReplay, switchMap, tap } from "rxjs/operators";
import { APIService, KPI } from "../../@core/api";
import { PerimeterClusterFilter } from "../perimeter-cluster-filter";
import { PerimeterFilter, PerimeterPredicate } from "../perimeter-filter";
import { PerimeterReferenceDataFilter } from "../perimeter-reference-data-filter";
import { idMapToArray, ScopeService } from "./scope.service";
import { UsersService } from "./users.service";
import { PERIMETER_FILTER_SELECTION_STORAGE_KEY, PREVIOUS_PERIMETERS, SELECTED_PERIMETERS } from "./constants";
import { ParentPerimeterTypeFilter } from "../parent-perimeter-type-filter";

@Injectable()
export class PerimetersService {
  public perimetersLoaded: boolean = false;
  private _previousPerimeters$: BehaviorSubject<KPI.Perimeter[]>;
  private _perimeters$: ReplaySubject<KPI.Perimeter[]>;
  private _allPerimeters$: ReplaySubject<KPI.Perimeter[]>;
  private _everySiteForAdministration$: Observable<KPI.Perimeter[]>;
  private _filteredPerimeters$: ReplaySubject<KPI.Perimeter[]>;
  private _allFilters$: ReplaySubject<PerimeterFilter[]>;
  private _perimeterBudgetPlans$: Observable<KPI.PerimeterBudgetPlan[]>;
  private perimeterBudgetPlansRefresh$ = new BehaviorSubject<void>(null);
  private _filtersLoading$: ReplaySubject<boolean>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: APIService,
    private translate: TranslateService,
    private users: UsersService,
    private scopeService: ScopeService
  ) {}

  public get filtersLoading$(): ReplaySubject<boolean> {
    if (!this._filtersLoading$) {
      this._filtersLoading$ = new ReplaySubject(1);
    }
    return this._filtersLoading$;
  }

  /** List of previously selected perimeters. */
  public get previousPerimeters$(): BehaviorSubject<KPI.Perimeter[]> {
    if (!this._previousPerimeters$) {
      this._previousPerimeters$ = new BehaviorSubject([]);
    }

    return this._previousPerimeters$;
  }

  /** List of selected perimeters. */
  public get perimeters$(): ReplaySubject<KPI.Perimeter[]> {
    if (!this._perimeters$) {
      this._perimeters$ = new ReplaySubject(1);

      // Subscribe to query params so that we can filter perimeters by id. in the URL
      this.route.queryParams
        .pipe(
          filter(params => params.perimeters !== undefined),
          map(params => (<string>params.perimeters).split(",").map(Number)),
          switchMap(perimetersIds =>
            this.allPerimeters$.pipe(
              map(perimeters =>
                perimeters.filter(perimeter => perimetersIds.some(perimeterId => perimeterId === perimeter.id))
              ),
            )
          )
        )
        .subscribe(perimeters => {
          this.updateSelectedPerimeters(perimeters);
        });
    }

    return this._perimeters$;
  }

  public siteByIdAllPerimeters(siteId: number): Observable<KPI.Perimeter> {
    return this.allPerimeters$.pipe(map(perimeters => perimeters.find(p => p.id === siteId)));
  }

  public get buildingIds$(): Observable<number[]> {
    return this.perimeters$.pipe(map(perimeters => PerimetersService.getBuildingsIds(perimeters)));
  }

  /** List of all available perimeters. */
  public get allPerimeters$(): ReplaySubject<KPI.Perimeter[]> {
    if (!this._allPerimeters$) {
      this._allPerimeters$ = new ReplaySubject(1);
      this.scopeService.perimeters$.pipe(idMapToArray()).subscribe(perimeters => {
        this._allPerimeters$.next(perimeters);
      });
    }

    return this._allPerimeters$;
  }

  public get everySiteForAdministration$(): Observable<KPI.Perimeter[]> {
    if (!this._everySiteForAdministration$) {
      this._everySiteForAdministration$ = this.users.isCurrentUserSuperAdmin$.pipe(
        switchMap(isSuperAdmin =>
          isSuperAdmin
            ? this.api
                .administration_perimeters()
                .pipe(map(perimeters => ScopeService.mapPerimetersFilterRefData(perimeters)))
            : this.allPerimeters$
        ),
        shareReplay(1)
      );
    }

    return this._everySiteForAdministration$;
  }

  /** List of filtered perimeters. */
  public get filteredPerimeters$(): ReplaySubject<KPI.Perimeter[]> {
    if (!this._filteredPerimeters$) {
      this._filteredPerimeters$ = new ReplaySubject(1);
      this.scopeService.perimeters$.pipe(idMapToArray()).subscribe(perimeters => {
        this._filteredPerimeters$.next(perimeters);
      });
    }

    return this._filteredPerimeters$;
  }

  /**
   * List of available filters.
   */
  public get allFilters$(): ReplaySubject<PerimeterFilter[]> {
    if (!this._filtersLoading$) {
      this._filtersLoading$ = new ReplaySubject(1);
    }
    this._filtersLoading$.next(true);
    if (!this._allFilters$) {
      this._allFilters$ = new ReplaySubject(1);
      this.allPerimeters$.subscribe(allPerimeters => {
        combineLatest(
          this.scopeService.perimeters$.pipe(idMapToArray()),
          this.scopeService.referenceFields$.pipe(idMapToArray()),
          this.scopeService.getCustomerClusters(allPerimeters),
          this.scopeService.perimeterTypes$.pipe(idMapToArray()),
          this.users.users$
        ).subscribe(([perimeters, referenceFields, clusters, perimeterTypes, users]) => {
          const filters: PerimeterFilter[] = referenceFields
            .filter(referenceField => referenceField.filtering_intelligence !== KPI.ReferenceFieldFilterType.FILTER_NO)
            .map(referenceField => new PerimeterReferenceDataFilter(referenceField, perimeters, users));
          filters.push(new PerimeterClusterFilter(this.translate.instant("Cluster"), clusters));

          const availableTypes: KPI.PerimeterType[] = [];
          perimeterTypes.forEach(type => {
            if (type.only_for_clusters.some(id => clusters.some(cluster => cluster.id === id)) && type.level === 1) {
              availableTypes.push(type);
            }
          });

          filters.push(new ParentPerimeterTypeFilter(this.translate.instant("Perimeter type"), availableTypes));
          this._allFilters$.next(filters);
          this._filtersLoading$.next(false);
        });
      });
    }

    return this._allFilters$;
  }

  /**
   * List of available filters, discard filters having only one value
   */
  public get filters$(): Observable<PerimeterFilter[]> {
    return combineLatest([this.allFilters$, this._perimeterFilterSelectionRefreshed$]).pipe(
      map(([perimeterFilters]) => {
        return perimeterFilters.filter(
          perimeterFilter =>
            perimeterFilter.availableValues.length > 1 || perimeterFilter.availableValuesRange.length > 1
        );
      })
    );
  }

  /**
   * Get the list of budget plans
   */
  public get perimeterBudgetPlans$(): Observable<KPI.PerimeterBudgetPlan[]> {
    if (!this._perimeterBudgetPlans$) {
      this._perimeterBudgetPlans$ = this.perimeterBudgetPlansRefresh$.pipe(
        switchMap(() => this.api.audit_perimeter_budget_plans()),
        map(budgetPlans => this.mapPerimeterBudgetPlan(budgetPlans)),
        share()
      );
    }

    return this._perimeterBudgetPlans$;
  }

  /**
   * Forces refresh of budget plan
   */
  public refreshPerimeterBudgetPlans(): void {
    this.perimeterBudgetPlansRefresh$.next();
  }

  /**
   * Creates a perimeter budget plan using the api
   *
   * @param plan
   */
  public postPerimeterBudgetPlan(plan: Partial<KPI.PerimeterBudgetPlan>): Observable<KPI.PerimeterBudgetPlan> {
    return this.api.audit_perimeter_budget_plans_post(plan).pipe(tap(() => this.refreshPerimeterBudgetPlans()));
  }

  /**
   * Patch a perimeter budget plan using the api
   *
   * @param plan
   */
  public patchPerimeterBudgetPlan(
    planId: number,
    plan: Partial<KPI.PerimeterBudgetPlan>
  ): Observable<KPI.PerimeterBudgetPlan> {
    return this.api
      .audit_perimeter_budget_plans_patch(planId, plan)
      .pipe(tap(() => this.refreshPerimeterBudgetPlans()));
  }

  /**
   * Recover the list of perimeter budget plan linked to filtered perimeters, can be filter using the params
   * @param params
   */
  public getPerimeterBudgetPlans(year?: number): Observable<KPI.PerimeterBudgetPlan[]> {
    return combineLatest(this.perimeters$, this.api.audit_perimeter_budget_plans({ years: year ? [year] : [] })).pipe(
      map(([perimeters, budgetPlans]) => {
        return budgetPlans.filter(budgetPlan => {
          for (const perim of perimeters) {
            if (budgetPlan.perimeter === perim.id) {
              return true;
            }
          }
        });
      }),
      map(budgetPlans => this.mapPerimeterBudgetPlan(budgetPlans))
    );
  }

  /**
   * Gets the list of buildings ids. of a perimeter and it's filtered sub-perimeters.
   *
   * @param perimeters The perimeters to retrieve the buildings ids. from.
   */
  public static getBuildingsIds(perimeters: KPI.Perimeter[]): number[] {
    const buildingIds: number[] = [];
    perimeters.map(p => {
      if (p.building_id) buildingIds.push(p.building_id);
      p.sub_perimeters?.map(sp => {
        if (sp.building_id) buildingIds.push(sp.building_id);
      });
    });
    return [...new Set(buildingIds)];
  }

  /**
   * Gets the list of cluster ids. of the currently selected perimeters
   */
  public get clusterIds$(): Observable<number[]> {
    return this.perimeters$.pipe(map(perimeters => [...new Set(perimeters.map(p => p.cluster))]));
  }

  /**
   * Gets the list of perimeter and sub_perimeter ids.
   *
   * @param perimeters The perimeters to retrieve the ids. from.
   */
  public static getAllPerimetersIds(perimeters: KPI.Perimeter[]): number[] {
    const perimeterIds: number[] = [];
    perimeters.map(p => {
      perimeterIds.push(p.id);
      if (p.sub_perimeters && p.sub_perimeters.length) {
        perimeterIds.push(...p.sub_perimeters.map(sp => sp.id));
      }
    });
    return [...new Set(perimeterIds)];
  }

  /**
   * Recursively finds the list of perimeter in which a perimeter is nested into.
   *
   * @param id The perimeter id to retrieve.
   * @param perimeters The perimeters list.
   */
  public static findPerimetersList(id: number, perimeters: KPI.Perimeter[]): KPI.Perimeter[] {
    for (const perimeter of perimeters) {
      if (perimeter.id === id) {
        return [perimeter];
      }
      if (perimeter.sub_perimeters) {
        const matchingSubPerimeters = PerimetersService.findPerimetersList(id, perimeter.sub_perimeters);
        if (matchingSubPerimeters.length > 0) {
          return [].concat([perimeter], matchingSubPerimeters);
        }
      }
    }
    return [];
  }

  private _subPerimeter: Observable<KPI.Perimeter>;
  /**
   * Get the sub perimeter
   *
   * @param perimeter
   */
  public getsubPerimeter(id: number): Observable<KPI.Perimeter> {
    if (!this._subPerimeter) {
      this._subPerimeter = this.api.structures_perimeter(id).pipe(shareReplay(1));
    }
    return this._subPerimeter;
  }

  /**
   * Returns a nice label representing a list of perimeters.
   */
  public getPerimetersLabel(perimeters: KPI.Perimeter[]): Observable<string> {
    return perimeters.length === 1
      ? of(perimeters[0].name)
      : this.translate.get("core.utils.perimeters.selected_perimeters", { number: perimeters.length });
  }

  /**
   * Sets the selected perimeters.
   *
   * @param perimeters List of selected perimeters.
   */
  public updateSelectedPerimeters(perimeters: KPI.Perimeter[], previousPerimeters?: KPI.Perimeter[]): void {
    this.perimeters$.next(perimeters);
    if (previousPerimeters) {
      this.previousPerimeters$.next(previousPerimeters);
    } else {
      this.savePerimeters(
        perimeters.reduce((acc, p) => acc.concat([p.id, ...p.sub_perimeters?.map(({ id }) => id)]), [])
      );
    }
    this.perimetersHaveChanged();
  }

  /**
   * Filters the perimeters.
   *
   * @param input Optional string to match perimeter by name.
   */
  public filterPerimeters(input?: string): void {
    if (!this._filtersLoading$) {
      this._filtersLoading$ = new ReplaySubject(1);
    }
    this._filtersLoading$.next(true);
    combineLatest(this.allPerimeters$, this.filters$)
      .pipe(first())
      .subscribe(([perimeters, filters]) => {
        // Make a clone of the perimeters because we'll mangle the `sub_perimeters` list when filtering
        const clonedPerimeters = perimeters.map((perimeter: KPI.Perimeter) => R.clone(perimeter));

        const enabledFilters = filters.filter(f => f.hasSelected());
        const inputPredicate = input ? this.getInputPredicate(input) : null;

        const predicate = R.allPass(enabledFilters.map(f => f.getPredicate(f.getSelectedValues())));

        const filteredPerimeters = this.applyPredicate(clonedPerimeters, predicate, inputPredicate).map(p => ({
          ...p,
          sub_perimeters: this.applyPredicate(p.sub_perimeters, predicate, inputPredicate)
        }));
        this.filteredPerimeters$.next(filteredPerimeters);
        this._filtersLoading$.next(false);
      });
  }

  public savePerimeterFilterSelection(filters: PerimeterFilter[]): void {
    const selectedFilters = filters.filter(filter => filter.hasSelected());
    localStorage.setItem(PERIMETER_FILTER_SELECTION_STORAGE_KEY, JSON.stringify(selectedFilters));
    this.refreshPerimeterFilterSelection();
  }

  private _perimeterFilterSelectionRefreshed$ = new BehaviorSubject<void>(null);
  private refreshPerimeterFilterSelection(): void {
    this._perimeterFilterSelectionRefreshed$.next();
  }

  public get perimeterFilterSelection(): PerimeterFilter[] {
    try {
      return JSON.parse(localStorage.getItem(PERIMETER_FILTER_SELECTION_STORAGE_KEY)) || [];
    } catch (error) {
      return [];
    }
  }

  public get activePerimeterFilters$(): Observable<PerimeterFilter[]> {
    return this.filters$.pipe(
      map(filters => {
        const savedSelections = this.perimeterFilterSelection;
        return filters
          .map(filter => {
            const savedSelection = savedSelections?.find(f => f.label === filter.label);
            if (savedSelection) {
              filter.selectedValues = savedSelection.selectedValues;
              filter.selectedValueRange = savedSelection.selectedValueRange;
            }
            return filter;
          })
          .filter(f => f.hasSelected());
      })
    );
  }

  private _perimeterPictures: ObservableMap<KPI.PerimeterPicture[]> = {};
  /**
   * Get the perimeter pictures
   *
   * @param perimeter
   */
  public getPerimeterPictures(perimeter: KPI.Perimeter): Observable<KPI.PerimeterPicture[]> {
    if (!this._perimeterPictures[perimeter.id]) {
      this._perimeterPictures[perimeter.id] = this.api
        .structures_perimeter(perimeter.id, { fields: 'id,pictures' })
        .pipe(map(perimeter => perimeter.pictures))
        .pipe(shareReplay(1));
    }
    return this._perimeterPictures[perimeter.id];
  }

  private _perimeterPictureUrl: ObservableMap<string> = {};
  public getPerimeterPicture(perimeter: KPI.Perimeter): Observable<string> {
    if (!this._perimeterPictureUrl[perimeter.id]) {
      this._perimeterPictureUrl[perimeter.id] = this.getPerimeterPictures(perimeter)
        .pipe(
          map(pictures => {
            if (pictures?.length) {
              if (perimeter.cover_image) {
                const perimeterCoverImage = pictures.find(p => p.id === perimeter.cover_image);
                if (perimeterCoverImage) {
                  return perimeterCoverImage.picture;
                } else {
                  return pictures[0].picture;
                }
              } else {
                return pictures[0].picture;
              }
            } else {
              return null;
            }
          })
        )
        .pipe(shareReplay(1));
    }
    return this._perimeterPictureUrl[perimeter.id];
  }

  /**
   * Get multiple perimeter pictures
   *
   * @param perimeter
   */
  public getPerimetersPictures(perimeters: KPI.Perimeter[]): Observable<KPI.PerimeterPicture[]> {
    return combineLatest(
      ...perimeters.map(perimeter => this.getPerimeterPictures(perimeter))
      // @ts-ignore
    ).pipe(map(pictures => pictures.flat(1)));
  }

  /**
   * Map perimeter budget plan
   *
   * @param plans
   */
  private mapPerimeterBudgetPlan(plans: KPI.PerimeterBudgetPlan[]): KPI.PerimeterBudgetPlan[] {
    return plans.map(plan => {
      plan.close_date_obj = plan.close_date ? new Date(plan.close_date) : null;
      return plan;
    });
  }

  /**
   * Applies a predicate to a list of perimeters and recursively to their sub-perimeters.
   *
   * @param perimeters The perimeters to test.
   * @param predicate The predicate to match.
   * @param inputPredicate Optional search input predicate.
   */
  private applyPredicate(
    perimeters: KPI.Perimeter[],
    predicate: PerimeterPredicate,
    inputPredicate?: PerimeterPredicate
  ): KPI.Perimeter[] {
    const filteredPerimeters = [];
    if (perimeters && perimeters.length) {
      perimeters.filter(p => {
        const filteredSubPerimeters = this.applyPredicate(
          p.sub_perimeters?.map(sp => ({
            ...sp,
            levelParentPerimeterType: sp.level_parent
              ? p.sub_perimeters.find(({ id }) => id === sp.level_parent)?.perimeter_type
              : null
          })),
          predicate
        );

        if ((!inputPredicate || inputPredicate(p)) && (filteredSubPerimeters.length > 0 || predicate(p))) {
          // Notice here that we override `sub_perimeters` with a filtered list
          // We can do that because we cloned the "true" perimeters list, so that
          // we can start fresh on the next filtering operation.
          p.sub_perimeters = filteredSubPerimeters;
          filteredPerimeters.push(p);
        }
      });
    }

    return filteredPerimeters;
  }

  /**
   * Returns a predicate matching a perimeter by name.
   *
   * @param input The input to match.
   */
  private getInputPredicate(input: string): PerimeterPredicate {
    const sanitizedInput = deburr(input.toLowerCase());

    return (p: KPI.Perimeter) => {
      const sanitizedName = deburr(p.name.toLowerCase());
      return sanitizedName.includes(sanitizedInput);
    };
  }

  public retriveSavedPerimeters(): [number[], number[]] {
    try {
      return [
        JSON.parse(localStorage.getItem(SELECTED_PERIMETERS)),
        JSON.parse(localStorage.getItem(PREVIOUS_PERIMETERS))
      ];
    } catch (error) {
      return;
    }
  }

  public getSelectedPerimeters(allPerimeters: KPI.Perimeter[], perimeterIds: number[]): KPI.Perimeter[] {
    return allPerimeters
      .filter(p => perimeterIds.includes(p.id))
      .map(p => ({
        ...p,
        sub_perimeters: p.sub_perimeters.filter(sp => perimeterIds.includes(sp.id))
      }));
  }

  public loadPerimeters(forcedPerimetersIds?: number[]): Subscription {
    return this.scopeService.perimeters$.pipe(idMapToArray()).subscribe(perimeters => {
      if (forcedPerimetersIds && forcedPerimetersIds.length) {
        const forcedPerimeters = perimeters.filter(p => forcedPerimetersIds.includes(p.id));
        this.updateSelectedPerimeters(forcedPerimeters, []);
      } else {
        const [perimeterIds, previousPerimeterIds] = this.retriveSavedPerimeters();
        if (perimeterIds) {
          const savedPerimeters = this.getSelectedPerimeters(perimeters, perimeterIds);
          if (previousPerimeterIds?.length) {
            const savedPreviousPerimeter = this.getSelectedPerimeters(perimeters, previousPerimeterIds);
            if (savedPreviousPerimeter?.length) {
              this.updateSelectedPerimeters(savedPerimeters, savedPreviousPerimeter);
            } else {
              this.updateSelectedPerimeters(savedPerimeters, []);
            }
          } else {
            this.updateSelectedPerimeters(savedPerimeters, []);
          }
        } else {
          this.updateSelectedPerimeters(perimeters, []);
        }
      }

      this.perimetersLoaded = true;
    });
  }

  public setFocusedPerimeter(perimeters: number[]) {
    localStorage.setItem(PREVIOUS_PERIMETERS, localStorage.getItem(SELECTED_PERIMETERS));
    localStorage.setItem(SELECTED_PERIMETERS, JSON.stringify(perimeters));
    this.loadPerimeters();
  }

  public goBackToPreviousPerimeters() {
    localStorage.setItem(SELECTED_PERIMETERS, localStorage.getItem(PREVIOUS_PERIMETERS));
    localStorage.removeItem(PREVIOUS_PERIMETERS);
    this.loadPerimeters();
  }

  private savePerimeters(perimeterIds: number[]) {
    localStorage.setItem(SELECTED_PERIMETERS, JSON.stringify(perimeterIds));
    localStorage.removeItem(PREVIOUS_PERIMETERS);
  }

  public updateUrlWithSelectedPerimeters(perimeters: KPI.Perimeter[]): void {
    const perimeterIds = this.getPerimeterIds(perimeters);
    this.route.queryParams.pipe(first()).subscribe(currentParams => {
      const newParams = { ...currentParams, perimeters: perimeterIds.join(",") };
      this.router.navigate([], { queryParams: newParams });
    });
  }

  private getPerimeterIds(perimeters: KPI.Perimeter[]): number[] {
    return perimeters.reduce((acc, p) => {
      acc.push(p.id);
      if (p.sub_perimeters) {
        acc.push(...p.sub_perimeters.map(sp => sp.id));
      }
      return acc;
    }, [] as number[]);
  }

  public addMultiPerimeter(perimeterValues: FormData): Observable<KPI.Perimeter> {
    return this.api.add_multi_perimeter(perimeterValues);
  }

  public updateMultiPerimeter(siteId: number, perimeterValues: FormData): Observable<KPI.Perimeter> {
    return this.api.update_multi_perimeter(siteId, perimeterValues);
  }

  public addMonoPerimeter(siteId: number, perimeterValues: Partial<KPI.Perimeter>)
  public addMonoPerimeter(
    siteId: number,
    perimeterValues: { property: string; value: any }[]
  )
  public addMonoPerimeter(
    siteId: number,
    perimeterValues: Partial<KPI.Perimeter> | { property: string; value: any }[]
  ): Observable<KPI.Perimeter> {
    let perimeter: Partial<KPI.Perimeter>;
    if (Array.isArray(perimeterValues)) {
      perimeter = perimeterValues.reduce((acc, p) => ({ ...acc, [p.property]: p.value }), {});
    } else {
      perimeter = perimeterValues
    }
    return this.api.add_mono_perimeter(siteId, perimeter);
  }

  public updateMonoPerimeter(perimeterId: number, data: Partial<KPI.Perimeter>): Observable<KPI.Perimeter>;
  public updateMonoPerimeter(perimeterId: number, data: { property: string; value: any }[]): Observable<KPI.Perimeter>;
  public updateMonoPerimeter(
    perimeterId: number,
    data: { property: string; value: any }[] | Partial<KPI.Perimeter>,
  ): Observable<KPI.Perimeter> {
    let perimeter: Partial<KPI.Perimeter>;
    if (Array.isArray(data)) {
      perimeter = data.reduce((acc, p) => ({ ...acc, [p.property]: p.value }), {});
    } else {
      perimeter = data
    }
    return this.api.update_mono_perimeter(perimeterId, perimeter);
  }

  public deleteMonoPerimeter(perimeterId: number): Observable<void> {
    return this.api.delete_mono_perimeter(perimeterId);
  }

  public hasDeletionBlockingObjects(perimeterId: number, buildingIds: number[]): Observable<boolean> {
    const buildings = buildingIds.join(',');
    return combineLatest([
      buildingIds.length ? this.api.assets_assets({ buildings, fields: 'id' } as any) : of([]),
      buildingIds.length ? this.api.investments_investments({ buildings }) : of([]),
      this.api.get_initiatives({ perimeters: String(perimeterId) }),
    ]).pipe(
      map(([assets, investments, initiatives]) =>
        (assets?.length > 0) || (investments?.length > 0) || (initiatives?.length > 0)
      )
    );
  }

  public _perimetersHaveChanged$ = new BehaviorSubject<void>(undefined);
  public perimetersHaveChanged() {
    this._perimetersHaveChanged$.next();
  }

  public readonly _perimeterPicturesRefresh$ = new Subject<{ perimeterId: number; coverPictureUrl: string | null }>();
  public notifyPictureChanged(perimeterId: number, coverPictureUrl: string | null): void {
    delete this._perimeterPictures[perimeterId];
    delete this._perimeterPictureUrl[perimeterId];
    this._perimeterPicturesRefresh$.next({ perimeterId, coverPictureUrl });
  }

  public readonly _perimeterDataRefresh$ = new Subject<{ perimeterId: number; data: Partial<KPI.Perimeter> }>();
  public notifyPerimeterDataChanged(perimeterId: number, data: Partial<KPI.Perimeter>): void {
    this._perimeterDataRefresh$.next({ perimeterId, data });
  }

  private _selectedPerimetersMap$ = new BehaviorSubject<{ [id: number]: boolean }>({});
  public get selectedPerimetersMap$(): BehaviorSubject<{ [id: number]: boolean }> {
    return this._selectedPerimetersMap$;
  }

  public updateSelectedPerimetersMap(perimeters: KPI.Perimeter[], selectedPerimeters: KPI.Perimeter[]): void {
    const newMap: { [id: number]: boolean } = {};
    perimeters.forEach(perimeter => {
      const selectedPerimeterIndex = selectedPerimeters.findIndex(p => p.id === perimeter.id);
      const currentMap = this._selectedPerimetersMap$.value;
      newMap[perimeter.id] =
        currentMap[perimeter.id] !== undefined ? currentMap[perimeter.id] : selectedPerimeterIndex > -1;
    });

    this._selectedPerimetersMap$.next(newMap);
  }

  public setPerimeterSelection(perimeterId: number, selected: boolean): void {
    const currentMap = this._selectedPerimetersMap$.value;
    this._selectedPerimetersMap$.next({
      ...currentMap,
      [perimeterId]: selected
    });
  }

  public addToSelectedPerimeters(perimeterId: number): void {
    try {
      this.allPerimeters$.pipe(first()).subscribe(allPerimeters => {
        try {
          const perimeterToAdd = allPerimeters.find(p => p.id === perimeterId);
          if (perimeterToAdd) {
            this.perimeters$.pipe(first()).subscribe(currentSelectedPerimeters => {
              try {
                if (!currentSelectedPerimeters.find(p => p.id === perimeterId)) {
                  const updatedSelectedPerimeters = [...currentSelectedPerimeters, perimeterToAdd];
                  this.updateSelectedPerimeters(updatedSelectedPerimeters);
                  this.setPerimeterSelection(perimeterId, true);
                  const currentSelectionMap = this._selectedPerimetersMap$.value;
                  const allPerimetersSelected = allPerimeters.every(p => currentSelectionMap[p.id] === true);
                  if (allPerimetersSelected) {
                    this.setPerimeterSelection(perimeterId, true);
                  }
                  this.route.queryParams.pipe(first()).subscribe(currentParams => {
                    const existingPerimeters = currentParams.perimeters
                      ? currentParams.perimeters.split(",").map(Number)
                      : [];
                    const newPerimeterIds = this.getPerimeterIds([perimeterToAdd]);
                    const updatedIds = [...new Set([...existingPerimeters, ...newPerimeterIds])];
                    const newParams = { ...currentParams, perimeters: updatedIds.join(",") };
                    this.router.navigate([], { queryParams: newParams });
                    this.setPerimeterSelection(perimeterId, true);
                  });
                }
              } catch (error) {
                console.error("Error in perimeters$ subscription:", error);
              }
            });
          } else {
            setTimeout(() => {
              this.addToSelectedPerimeters(perimeterId);
            }, 1000);
          }
        } catch (error) {
          console.error("Error in allPerimeters$ subscription:", error);
        }
      });
    } catch (error) {
      console.error("Error in addToSelectedPerimeters:", error);
    }
  }

  public toggleAllPerimeters(perimeters: KPI.Perimeter[], selected: boolean): void {
    const currentMap = this._selectedPerimetersMap$.value;
    const newMap = { ...currentMap };
    perimeters.forEach(perimeter => {
      newMap[perimeter.id] = selected;
    });
    this._selectedPerimetersMap$.next(newMap);
  }

  private _sitePerimeterFocused$: { [siteId: number]: BehaviorSubject<KPI.Perimeter> } = {};
  public getSitePerimeterFocused(site: KPI.Perimeter): BehaviorSubject<KPI.Perimeter> {
    if (!this._sitePerimeterFocused$[site.id]) {
      this._sitePerimeterFocused$[site.id] = new BehaviorSubject<KPI.Perimeter>(site);
    }
    return this._sitePerimeterFocused$[site.id];
  }

  public setSitePerimeterFocused(siteId: number, perimeter: KPI.Perimeter) {
    if (!this._sitePerimeterFocused$[siteId]) {
      this._sitePerimeterFocused$[siteId] = new BehaviorSubject<KPI.Perimeter>(perimeter);
    } else {
      this._sitePerimeterFocused$[siteId].next(perimeter);
    }
  }

  public deletePerimete(perimeterId: number) {
    return this.api.delete_perimeter_by_id(perimeterId);
  }
}
